import React, { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import {
  AnimatedCard,
  ActionCard,
  ActionTitle,
  TextInput,
  TextArea,
  ActionRow,
  Label,
  SelectInput,
  NoraAskButton,
} from "./styles";

export default function ActionStep({
  themeColors,
  isFadingOut,
  action,
  index,
  total,
  onChangeTitle,
  onChangeDescription,
  onChangeDate,
  onSuggestAction,
  suggestionLoading,
  hasExplanation,
  onOpenExplanation,
  language = 'EN',
}) {
  const [selectedOption, setSelectedOption] = useState('');

  // Keep the timeframe select in sync with the stored date on action change
  useEffect(() => {
    if (!action) {
      setSelectedOption('');
      return;
    }
    if (!action.date) {
      setSelectedOption('');
      return;
    }
    const today = new Date();
    const d2w = formatDateYYYYMMDD(addDays(today, 14));
    const d1m = formatDateYYYYMMDD(addMonths(today, 1));
    const d2m = formatDateYYYYMMDD(addMonths(today, 2));
    const d3m = formatDateYYYYMMDD(addMonths(today, 3));
    const match = action.date === d2w
      ? '2w'
      : action.date === d1m
      ? '1m'
      : action.date === d2m
      ? '2m'
      : action.date === d3m
      ? '3m'
      : '';
    setSelectedOption(match);
  }, [action?.id, action?.date]);

  function formatDateYYYYMMDD(dateObj) {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function addDays(base, days) {
    const d = new Date(base.getTime());
    d.setDate(d.getDate() + days);
    return d;
  }

  function addMonths(base, months) {
    const d = new Date(base.getTime());
    d.setMonth(d.getMonth() + months);
    return d;
  }

  function handleIntervalChange(val) {
    setSelectedOption(val);
    if (!val) {
      // cleared selection
      onChangeDate(action.id, "");
      return;
    }
    const today = new Date();
    let due;
    switch (val) {
      case '2w':
        due = addDays(today, 14);
        break;
      case '1m':
        due = addMonths(today, 1);
        break;
      case '2m':
        due = addMonths(today, 2);
        break;
      case '3m':
        due = addMonths(today, 3);
        break;
      default:
        due = null;
    }
    if (due) {
      onChangeDate(action.id, formatDateYYYYMMDD(due));
    }
  }

  const copy = {
    EN: {
      actionOf: 'Action',
      generating: 'Generating…',
      differentSuggestion: 'Generate another recommendation',
      suggestAction: 'AI Recommendation',
      seeExplanation: 'See explanation',
      title: 'Title',
      titlePh: 'Action title',
      description: 'Description',
      descriptionPh: 'Add details to clarify the action',
      dueDate: 'Timeframe',
      selectTimeframe: 'Select timeframe',
      twoWeeks: '2 weeks',
      oneMonth: '1 month',
      twoMonths: '2 months',
      threeMonths: '3 months',
    },
    FR: {
      actionOf: 'Action',
      generating: 'Génération…',
      differentSuggestion: 'Générer une autre recommandation',
      suggestAction: 'Recommandation IA',
      seeExplanation: "Voir l’explication",
      title: 'Titre',
      titlePh: "Titre de l’action",
      description: 'Description',
      descriptionPh: "Ajoutez des détails pour clarifier l’action",
      dueDate: 'Temps',
      selectTimeframe: 'Sélectionner une échéance',
      twoWeeks: '2 semaines',
      oneMonth: '1 mois',
      twoMonths: '2 mois',
      threeMonths: '3 mois',
    },
    ES: {
      actionOf: 'Acción',
      generating: 'Generando…',
      differentSuggestion: 'Generar otra recomendación',
      suggestAction: 'Recomendación IA',
      seeExplanation: 'Ver explicación',
      title: 'Título',
      titlePh: 'Título de la acción',
      description: 'Descripción',
      descriptionPh: 'Agrega detalles para aclarar la acción',
      dueDate: 'Tiempo',
      selectTimeframe: 'Seleccionar plazo',
      twoWeeks: '2 semanas',
      oneMonth: '1 mes',
      twoMonths: '2 meses',
      threeMonths: '3 meses',
    },
  };
  const t = copy[language] || copy.EN;

  return (
    <AnimatedCard isFadingOut={isFadingOut}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 14, color: "#6b7280" }}>
          {t.actionOf} {index + 1} of {total}
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <NoraAskButton
            themeColors={themeColors}
            onClick={onSuggestAction}
            disabled={!!suggestionLoading}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <Sparkles size={16} style={{ marginRight: 6 }} />
              {t.suggestAction}
            </span>
          </NoraAskButton>
        </div>
      </div>
      <ActionCard>
        <div style={{ marginBottom: 8 }}>
          <ActionTitle style={{ marginBottom: 6 }}>{t.title}</ActionTitle>
          <TextInput
            value={action.title}
            onChange={(e) => onChangeTitle(action.id, e.target.value)}
            placeholder={t.titlePh}
          />
        </div>
        <div style={{ marginBottom: 8 }}>
          <ActionTitle style={{ marginBottom: 6 }}>{t.description}</ActionTitle>
          <TextArea
            value={action.description || ''}
            onChange={(e) => onChangeDescription(action.id, e.target.value)}
            placeholder={t.descriptionPh}
          />
        </div>
        <ActionRow>
          <Label>
            <span style={{ marginRight: 8 }}>{t.dueDate}</span>
            <SelectInput value={selectedOption} onChange={(e) => handleIntervalChange(e.target.value)}>
              <option value="">{t.selectTimeframe}</option>
              <option value="2w">{t.twoWeeks}</option>
              <option value="1m">{t.oneMonth}</option>
              <option value="2m">{t.twoMonths}</option>
              <option value="3m">{t.threeMonths}</option>
            </SelectInput>
          </Label>
        </ActionRow>
      </ActionCard>
    </AnimatedCard>
  );
}

